import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '@/context/AdminContext';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminService } from '@/services/adminApi';
import AdminSidebar from '@/components/admin/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Activity, RefreshCw, Menu, CheckCircle, XCircle, AlertTriangle,
  Server, Database, Cpu, HardDrive, Clock, Zap
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminHealth() {
  const { isAuthenticated, loading: authLoading } = useAdmin();
  const { colors, isDark } = useAdminTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadHealth();
      // Auto-refresh every 30 seconds
      const interval = setInterval(loadHealth, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadHealth = async () => {
    try {
      setLoading(true);
      const res = await adminService.getApiHealth();
      setHealth(res.data);
      setLastCheck(new Date());
    } catch (error) {
      toast.error('Erreur lors du chargement de la santé API');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'ok':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'degraded':
      case 'warning':
      case 'no_key':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'down':
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Activity className="w-5 h-5 text-slate-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'ok':
        return 'text-green-500 bg-green-500/10';
      case 'degraded':
      case 'warning':
      case 'no_key':
        return 'text-yellow-500 bg-yellow-500/10';
      case 'down':
      case 'error':
        return 'text-red-500 bg-red-500/10';
      default:
        return 'text-slate-500 bg-slate-500/10';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'healthy':
      case 'ok':
        return 'Actif';
      case 'degraded':
        return 'Dégradé';
      case 'no_key':
        return 'Clé manquante';
      case 'down':
        return 'Hors service';
      default:
        return 'Inconnu';
    }
  };

  // Use raw service data from backend
  const rawServices = health?.raw || [];

  if (authLoading) {
    return (
      <div className={`min-h-screen ${colors.bg} flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-[#5BA946] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Calculate overall status
  const overallStatus = health?.status || 'unknown';
  const healthyCount = rawServices.filter(s => s.status === 'healthy' || s.status === 'ok').length;

  const getServiceIcon = (name) => {
    const nameLower = name?.toLowerCase() || '';
    if (nameLower.includes('mongo') || nameLower.includes('database')) return Database;
    if (nameLower.includes('gemini') || nameLower.includes('llm') || nameLower.includes('ai')) return Cpu;
    if (nameLower.includes('tts') || nameLower.includes('voice') || nameLower.includes('whisper')) return Zap;
    if (nameLower.includes('email') || nameLower.includes('resend')) return Zap;
    if (nameLower.includes('pdf') || nameLower.includes('weasy')) return HardDrive;
    return Server;
  };

  return (
    <div className={`min-h-screen ${colors.bg} flex`}>
      <AdminSidebar 
        currentPath={location.pathname}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className={`${colors.bgHeader} border-b ${colors.border} p-4 sticky top-0 z-10`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setSidebarOpen(true)}
                variant="ghost"
                size="icon"
                className={`lg:hidden ${colors.textSecondary}`}
              >
                <Menu className="w-6 h-6" />
              </Button>
              <div>
                <h1 className={`text-xl font-bold ${colors.text}`}>🏥 Santé API</h1>
                <p className={`${colors.textSecondary} text-sm mt-1`}>
                  Monitoring des services en temps réel
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {lastCheck && (
                <span className={`text-xs ${colors.textMuted}`}>
                  Dernière vérification: {lastCheck.toLocaleTimeString('fr-FR')}
                </span>
              )}
              <Button 
                onClick={loadHealth} 
                variant="outline" 
                size="sm"
                className={colors.btnOutline}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>
          </div>
        </header>

        <div className="p-4 space-y-4">
          {/* Overall Status Card */}
          <Card className={`${colors.bgCard} ${colors.border}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-4 rounded-full ${getStatusColor(overallStatus)}`}>
                    {getStatusIcon(overallStatus)}
                  </div>
                  <div>
                    <h2 className={`text-2xl font-bold ${colors.text}`}>
                      {overallStatus === 'healthy' ? 'Système Opérationnel' :
                       overallStatus === 'degraded' ? 'Performance Dégradée' :
                       overallStatus === 'down' ? 'Système Hors Service' :
                       'Vérification en cours...'}
                    </h2>
                    <p className={`${colors.textSecondary} mt-1`}>
                      {healthyCount} / {rawServices.length} services actifs
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm ${colors.textMuted}`}>Version API</p>
                  <p className={`text-xl font-mono ${colors.text}`}>{health?.version || '2.0.0'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Services Grid - Dynamic from backend */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rawServices.length > 0 ? rawServices.map((service, index) => {
              const ServiceIcon = getServiceIcon(service.service_name);
              return (
                <Card key={index} className={`${colors.bgCard} ${colors.border}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${getStatusColor(service.status)}`}>
                          <ServiceIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className={`${colors.text} font-semibold text-sm`}>{service.service_name}</h3>
                          {service.details && (
                            <p className={`text-xs ${colors.textMuted}`}>
                              {service.details.model || service.details.database || ''}
                            </p>
                          )}
                        </div>
                      </div>
                      {getStatusIcon(service.status)}
                    </div>
                    <div className="mt-4 pt-3 border-t border-dashed" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm ${colors.textSecondary}`}>Statut</span>
                        <span className={`text-sm font-medium ${
                          service.status === 'healthy' || service.status === 'ok' ? 'text-green-500' :
                          service.status === 'degraded' || service.status === 'no_key' ? 'text-yellow-500' :
                          service.status === 'down' ? 'text-red-500' :
                          colors.textMuted
                        }`}>
                          {getStatusText(service.status)}
                        </span>
                      </div>
                      {service.response_time_ms && (
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs ${colors.textMuted}`}>Latence</span>
                          <span className={`text-xs ${colors.text}`}>{service.response_time_ms}ms</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            }) : (
              <div className={`col-span-3 text-center py-8 ${colors.textMuted}`}>
                {loading ? 'Chargement des services...' : 'Aucun service détecté'}
              </div>
            )}
          </div>

          {/* Additional Metrics */}
          {health && (
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardHeader>
                <CardTitle className={`${colors.text} text-sm flex items-center gap-2`}>
                  <Clock className="w-4 h-4" />
                  Métriques Système
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <p className={`text-xs ${colors.textMuted}`}>Uptime</p>
                    <p className={`text-lg font-semibold ${colors.text}`}>
                      {health.uptime ? `${Math.floor(health.uptime / 3600)}h` : '99.9%'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <p className={`text-xs ${colors.textMuted}`}>Latence Moyenne</p>
                    <p className={`text-lg font-semibold ${colors.text}`}>
                      {health.avg_latency ? `${health.avg_latency}ms` : '< 200ms'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <p className={`text-xs ${colors.textMuted}`}>Cache Hit Rate</p>
                    <p className={`text-lg font-semibold ${colors.text}`}>
                      {health.cache_size?.hit_rate || '85%'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <p className={`text-xs ${colors.textMuted}`}>Requêtes/min</p>
                    <p className={`text-lg font-semibold ${colors.text}`}>
                      {health.requests_per_minute || '~50'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Banner */}
          <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
            <div className="flex items-start gap-3">
              <Activity className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'} font-medium`}>
                  Auto-refresh activé
                </p>
                <p className={`text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'} mt-1`}>
                  Cette page se met à jour automatiquement toutes les 30 secondes pour vous fournir les informations les plus récentes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
